<template>
  <div class="task-files-list" data-testid="task-files-list">
    <FilesRequestsEmptyState
      v-if="!displayFiles.length"
      variant="files"
      data-testid="task-files-list__empty"
    />

    <div
      v-for="(file, index) in displayFiles"
      :key="file.file_id || file._id || index"
      class="task-files-list__row"
      :data-testid="`task-files-list__row-${index}`"
    >
      <div class="task-files-list__row-left">
        <span
          class="task-files-list__type-badge"
          :class="`task-files-list__type-badge--${getBadgeVariant(file)}`"
          :data-testid="`task-files-list__type-${index}`"
        >
          {{ getFileExtension(file) }}
        </span>
        <span class="task-files-list__name" :data-testid="`task-files-list__name-${index}`">
          {{ file.file_name || file.modifiedname || 'Untitled file' }}
        </span>
      </div>

      <div class="task-files-list__row-right">
        <span
          v-if="file.uploaded_on"
          class="task-files-list__timestamp"
          :data-testid="`task-files-list__timestamp-${index}`"
        >
          {{ $nova.formatDateTime(file.uploaded_on, 'D MMM YYYY, h:mm A') }}
        </span>
        <button
          v-if="getFileDownloadId(file)"
          type="button"
          class="task-files-list__action-btn"
          aria-label="Download file"
          :data-testid="`task-files-list__download-${index}`"
          @click="$nova.downloadFile(getFileDownloadId(file))"
        >
          <img
            src="/img/icons/DownloadIcon.svg"
            alt=""
            aria-hidden="true"
            class="task-files-list__action-icon"
          />
        </button>
        <button
          v-if="canDeleteFile"
          type="button"
          class="task-files-list__action-btn"
          aria-label="Delete file"
          :disabled="isDeleting(file)"
          :data-testid="`task-files-list__delete-${index}`"
          @click="handleDeleteFile(file)"
        >
          <img
            src="/img/icons/fbc-icons/DeleteActions.svg"
            alt=""
            aria-hidden="true"
            class="task-files-list__action-icon"
          />
        </button>
      </div>
    </div>

    <div
      v-if="task?.remark || task?.approver_remark || task?.sign_off_remark || task?.resubmission_remark"
      class="task-files-list__remarks-group"
      data-testid="task-files-list__remarks-group"
    >
      <div
        v-if="task?.remark"
        class="task-files-list__remarks"
        data-testid="task-files-list__remarks"
      >
        <div class="task-files-list__remarks-label" data-testid="task-files-list__remarks-label">
          Preparer Remarks
        </div>
        <div
          class="task-files-list__remarks-text"
          data-testid="task-files-list__remarks-text"
          v-html="highlightHashtags(task.remark)"
        />
      </div>

      <div
        v-if="task?.approver_remark"
        class="task-files-list__remarks"
        data-testid="task-files-list__approver-remarks"
      >
        <div
          class="task-files-list__remarks-label"
          data-testid="task-files-list__approver-remarks-label"
        >
          Approver Remarks
        </div>
        <div
          class="task-files-list__remarks-text"
          data-testid="task-files-list__approver-remarks-text"
          v-html="highlightHashtags(task.approver_remark)"
        />
      </div>

      <div
        v-if="task?.sign_off_remark"
        class="task-files-list__remarks"
        data-testid="task-files-list__sign-off-remarks"
      >
        <div
          class="task-files-list__remarks-label"
          data-testid="task-files-list__sign-off-remarks-label"
        >
          Sign Off Remarks
        </div>
        <div
          class="task-files-list__remarks-text"
          data-testid="task-files-list__sign-off-remarks-text"
          v-html="highlightHashtags(task.sign_off_remark)"
        />
      </div>

      <div
        v-if="task?.resubmission_remark"
        class="task-files-list__remarks"
        data-testid="task-files-list__resubmission-remarks"
      >
        <div
          class="task-files-list__remarks-label"
          data-testid="task-files-list__resubmission-remarks-label"
        >
          {{ resubmissionRemarksLabel }}
        </div>
        <div
          class="task-files-list__remarks-text"
          data-testid="task-files-list__resubmission-remarks-text"
          v-html="highlightHashtags(task.resubmission_remark)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  extractFileIdsFromTask,
  getDisplayFileExtension,
  getFileBadgeVariant,
} from '@/modules/financialclose/utils/utils';
import {
  TASK_UPLOAD_FILE_TYPES,
  TASK_UPLOAD_FILE_TYPE_LABELS,
  RETURN_TO_PENDING,
} from '@/modules/financialclose/utils/constants';
import FilesRequestsEmptyState from './FilesRequestsEmptyState.vue';
import './TaskFilesList.scss';

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default {
  name: 'TaskFilesList',

  components: {
    FilesRequestsEmptyState,
  },

  props: {
    task: {
      type: Object,
      default: () => ({}),
    },
    canDeleteFile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      deleteFileIds: [],
    };
  },

  computed: {
    resubmissionRemarksLabel() {
      return RETURN_TO_PENDING.REMARKS_LABEL;
    },

    displayFiles() {
      if (Array.isArray(this.task?.upload_v2) && this.task.upload_v2.length > 0) {
        return this.task.upload_v2;
      }
      const fileIds = extractFileIdsFromTask(this.task);
      if (fileIds.length > 0) {
        return fileIds.map((id) => ({ file_id: id, file_name: id }));
      }
      return [];
    },
  },

  methods: {
    getFileDownloadId(file) {
      const id = file?.file_id || file?._id;
      return id && typeof id === 'string' ? id : '';
    },

    getFileName(file) {
      return file.file_name || file.modifiedname || '';
    },

    getFileExtension(file) {
      if (file?.file_type === TASK_UPLOAD_FILE_TYPES.RECON) {
        return TASK_UPLOAD_FILE_TYPE_LABELS[TASK_UPLOAD_FILE_TYPES.RECON];
      }
      return getDisplayFileExtension(this.getFileName(file));
    },

    getBadgeVariant(file) {
      return getFileBadgeVariant(this.getFileExtension(file));
    },

    highlightHashtags(text) {
      if (!text) return '';
      const escaped = escapeHtml(text);
      return escaped.replace(/(#\w+)/g, '<span class="task-files-list__hashtag">$1</span>');
    },

    isDeleting(file) {
      const fileId = file?.file_id || file?._id;
      return this.deleteFileIds.includes(fileId);
    },

    async handleDeleteFile(file) {
      const fileId = file?.file_id || file?._id;
      if (!fileId || this.isDeleting(file)) return;

      this.deleteFileIds.push(fileId);
      try {
        const result = await this.$store.dispatch('tasks/deleteFileFromTask', {
          file,
          task: this.task,
        });
        if (result?.success) {
          this.$emit('refresh');
        }
      } finally {
        this.deleteFileIds = this.deleteFileIds.filter((id) => id !== fileId);
      }
    },
  },
};
</script>

